/// <reference types="vite/client" />
/**
 * Telemetry & Performance Monitoring Service
 * Manages buffering, batching, and dispatching of frontend & native telemetry.
 */

import type {
  TelemetryPayload,
  WebVitalMetric,
  ApiLatencyMetric,
  JsErrorMetric,
  NavigationTimingMetric,
  PerformanceMarkMetric,
  NativeStartupMetric,
  NativeThreadMetric,
  NativeMemoryMetric,
  NativeCrashMetric,
  NativeNetworkFailureMetric,
} from './telemetryTypes';
import { logger } from '../logging/logger';

class TelemetryService {
  private buffer: TelemetryPayload[] = [];
  private flushInterval: number | null = null;
  private readonly maxBufferSize = 30;
  private readonly flushIntervalMs = 10_000;
  private activeMarks = new Map<string, number>();
  private isEnabled = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_ENABLE_TELEMETRY !== 'false' : true;

  constructor() {
    this.startPeriodicFlush();
    this.setupPageUnloadFlush();
  }

  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Record Core Web Vital (LCP, FID, CLS, INP, FCP, TTFB)
   */
  public recordWebVital(metric: Omit<WebVitalMetric, 'timestamp'>): void {
    if (!this.isEnabled) return;
    const payload: TelemetryPayload = {
      type: 'web_vital',
      data: { ...metric, timestamp: Date.now() },
    };
    this.enqueue(payload);
    logger.debug(`[Telemetry] WebVital: ${metric.name}=${metric.value.toFixed(2)} (${metric.rating})`);
  }

  /**
   * Record API Latency & HTTP performance
   */
  public recordApiLatency(metric: Omit<ApiLatencyMetric, 'timestamp'>): void {
    if (!this.isEnabled) return;
    const payload: TelemetryPayload = {
      type: 'api_latency',
      data: { ...metric, timestamp: Date.now() },
    };
    this.enqueue(payload);
    const isExpectedAuthProbe = metric.status === 401 && (metric.endpoint.includes('/auth/refresh') || metric.endpoint.includes('/auth/login'));
    if ((!metric.success && !isExpectedAuthProbe) || metric.durationMs > 1500) {
      logger.warn(`[Telemetry] Slow/Failed API: ${metric.method} ${metric.endpoint} (${metric.durationMs}ms, ${metric.status})`);
    }
  }

  /**
   * Record JavaScript errors and exceptions
   */
  public recordError(error: Omit<JsErrorMetric, 'timestamp'>): void {
    if (!this.isEnabled) return;
    const payload: TelemetryPayload = {
      type: 'js_error',
      data: { ...error, timestamp: Date.now() },
    };
    this.enqueue(payload);
    logger.error(`[Telemetry] JS Error captured: ${error.message}`);
  }

  /**
   * Record Uncaught Crashes (React Error Boundary or unhandled rejections)
   */
  public recordCrash(error: Error, options: { fatal?: boolean; componentStack?: string; context?: Record<string, unknown> } = {}): void {
    if (!this.isEnabled) return;
    const payload: TelemetryPayload = {
      type: 'js_error',
      data: {
        message: error.message || 'Unknown Crash',
        stack: error.stack,
        componentStack: options.componentStack,
        fatal: options.fatal ?? true,
        context: options.context,
        timestamp: Date.now(),
      },
    };
    this.enqueue(payload);
    logger.error(`[Telemetry] CRASH: ${error.message}`);
    // Flush immediately on fatal crash
    this.flush();
  }

  /**
   * Record Navigation Timing (Route transitions or initial page load)
   */
  public recordNavigation(metric: Omit<NavigationTimingMetric, 'timestamp'>): void {
    if (!this.isEnabled) return;
    const payload: TelemetryPayload = {
      type: 'navigation_timing',
      data: { ...metric, timestamp: Date.now() },
    };
    this.enqueue(payload);
    logger.debug(`[Telemetry] Nav Timing: ${metric.route} took ${metric.durationMs.toFixed(1)}ms`);
  }

  /**
   * User Timing API: Start a named performance mark
   */
  public startMark(markName: string): void {
    if (typeof performance !== 'undefined' && performance.mark) {
      try {
        performance.mark(`${markName}-start`);
      } catch {
        // Safe fallback
      }
    }
    this.activeMarks.set(markName, performance?.now ? performance.now() : Date.now());
  }

  /**
   * User Timing API: End a named performance mark and log measure
   */
  public endMark(markName: string, metadata?: Record<string, unknown>): void {
    const startTime = this.activeMarks.get(markName);
    if (!startTime) return;
    const endTime = performance?.now ? performance.now() : Date.now();
    const durationMs = endTime - startTime;
    this.activeMarks.delete(markName);

    if (typeof performance !== 'undefined' && performance.measure) {
      try {
        performance.measure(markName, `${markName}-start`);
      } catch {
        // Safe fallback
      }
    }

    const payload: TelemetryPayload = {
      type: 'performance_mark',
      data: {
        name: markName,
        durationMs,
        metadata,
        timestamp: Date.now(),
      },
    };
    this.enqueue(payload);
    logger.debug(`[Telemetry] Mark '${markName}': ${durationMs.toFixed(2)}ms`);
  }

  /**
   * React Native: Startup time monitoring
   */
  public recordNativeStartup(metric: Omit<NativeStartupMetric, 'timestamp'>): void {
    this.enqueue({
      type: 'native_startup',
      data: { ...metric, timestamp: Date.now() },
    });
  }

  /**
   * React Native: JS / UI thread frame rate and jank
   */
  public recordNativeThread(metric: Omit<NativeThreadMetric, 'timestamp'>): void {
    this.enqueue({
      type: 'native_thread',
      data: { ...metric, timestamp: Date.now() },
    });
  }

  /**
   * React Native: Memory usage tracking
   */
  public recordNativeMemory(metric: Omit<NativeMemoryMetric, 'timestamp'>): void {
    this.enqueue({
      type: 'native_memory',
      data: { ...metric, timestamp: Date.now() },
    });
  }

  /**
   * React Native: Native thread / bridge crash
   */
  public recordNativeCrash(metric: Omit<NativeCrashMetric, 'timestamp'>): void {
    this.enqueue({
      type: 'native_crash',
      data: { ...metric, timestamp: Date.now() },
    });
    this.flush();
  }

  /**
   * Network Failure diagnostics
   */
  public recordNetworkFailure(metric: Omit<NativeNetworkFailureMetric, 'timestamp'>): void {
    this.enqueue({
      type: 'native_network_failure',
      data: { ...metric, timestamp: Date.now() },
    });
    logger.warn(`[Telemetry] Network Failure: ${metric.errorType} for ${metric.url}`);
  }

  private enqueue(payload: TelemetryPayload): void {
    this.buffer.push(payload);
    if (this.buffer.length >= this.maxBufferSize) {
      this.flush();
    }
  }

  /**
   * Flush batched metrics to backend or APM
   */
  public flush(): void {
    if (!this.isEnabled || this.buffer.length === 0) return;
    const batch = [...this.buffer];
    this.buffer = [];

    const apiBase = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) || '';
    const endpoint = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TELEMETRY_ENDPOINT) || (apiBase ? `${apiBase}/api/v1/telemetry/metrics` : '/api/v1/telemetry/metrics');
    const payloadString = JSON.stringify({ events: batch, sentAt: Date.now() });

    // In browser: prefer sendBeacon if available, fallback to fetch/console
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payloadString], { type: 'application/json' });
      const ok = navigator.sendBeacon(endpoint, blob);
      if (ok) return;
    }

    if (typeof fetch !== 'undefined') {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payloadString,
        keepalive: true,
      }).catch(() => {
        // Silently discard on network failure to avoid recursive loops
      });
    }
  }

  private startPeriodicFlush(): void {
    if (typeof window !== 'undefined') {
      const globalWindow = window as unknown as { __telemetryFlushInterval?: number };
      if (globalWindow.__telemetryFlushInterval) {
        clearInterval(globalWindow.__telemetryFlushInterval);
      }
      this.flushInterval = window.setInterval(() => {
        this.flush();
      }, this.flushIntervalMs);
      globalWindow.__telemetryFlushInterval = this.flushInterval;
    }
  }

  private setupPageUnloadFlush(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
      window.addEventListener('pagehide', () => {
        this.flush();
      });
    }
  }
}

export const telemetryService = new TelemetryService();
export default telemetryService;
